/**
 * Группы и подгруппы меню.
 *
 * «Бар → Напитки → Горилла» — два уровня, и не больше. Третий уровень
 * выглядит соблазнительно на бумаге, но официант на бегу до него
 * не доберётся: он помнит блюдо, а не путь к нему. Для этого есть поиск.
 *
 * Подгруппа наследует от группы цех печати: бегунок «Напитков» уходит
 * на барный принтер, потому что так задано у «Бара», а не потому,
 * что кто-то вспомнил проставить это у каждой подгруппы.
 */

export type CatNode = {
  id: string;
  name: string;
  parentId: string | null;
  sort: number;
  station?: string | null;
  color?: string | null;
  isActive?: boolean;
};

export type CatTree = CatNode & { children: CatNode[] };

/**
 * Дерево из плоского списка. Осиротевшие подгруппы (родитель удалён
 * или выключен) поднимаются наверх: спрятать товары вместе с группой —
 * значит потерять их из меню молча.
 */
export function buildCatTree(cats: CatNode[]): CatTree[] {
  const byId = new Map(cats.map((c) => [c.id, c]));
  const roots: CatTree[] = [];
  const childrenOf = new Map<string, CatNode[]>();

  for (const c of cats) {
    const hasLiveParent = c.parentId !== null && byId.has(c.parentId);
    if (hasLiveParent) {
      const arr = childrenOf.get(c.parentId!) ?? [];
      arr.push(c);
      childrenOf.set(c.parentId!, arr);
    }
  }

  for (const c of cats) {
    const orphan = c.parentId !== null && !byId.has(c.parentId);
    if (c.parentId === null || orphan) {
      roots.push({
        ...c,
        children: (childrenOf.get(c.id) ?? []).sort(bySort),
      });
    }
  }
  return roots.sort(bySort);
}

function bySort(a: { sort: number; name: string }, b: { sort: number; name: string }): number {
  return a.sort - b.sort || a.name.localeCompare(b.name, 'ru');
}

/**
 * Цех печати с наследованием: у подгруппы своё значение важнее,
 * пустое — берём у родителя, нет и там — общий принтер.
 */
export function stationOf(cat: CatNode, parent: CatNode | null): string | null {
  return cat.station ?? parent?.station ?? null;
}

/**
 * Все категории ветки: сама группа и её подгруппы. Нужно для витрины
 * и кассы — нажали «Бар», показываем всё, что внутри, а не пустоту.
 */
export function branchIds(catId: string, cats: CatNode[]): string[] {
  const kids = cats.filter((c) => c.parentId === catId).map((c) => c.id);
  return [catId, ...kids];
}

/**
 * Можно ли сделать эту категорию подгруппой той.
 * Три запрета, каждый из практики:
 *  — сама себе родителем (случайный выбор в списке);
 *  — родителем своей же подгруппы (петля, после которой меню исчезает);
 *  — третий уровень (у выбранного родителя уже есть свой родитель).
 */
export function canNest(
  childId: string,
  parentId: string,
  cats: CatNode[],
): { ok: true } | { ok: false; reason: string } {
  if (childId === parentId) {
    return { ok: false, reason: 'Группа не может быть внутри самой себя' };
  }
  const parent = cats.find((c) => c.id === parentId);
  if (!parent) return { ok: false, reason: 'Такой группы нет' };

  if (parent.parentId !== null) {
    return {
      ok: false,
      reason: 'Больше двух уровней не бывает: «Бар → Напитки», и хватит',
    };
  }
  const hasChildren = cats.some((c) => c.parentId === childId);
  if (hasChildren) {
    return {
      ok: false,
      reason: 'В этой группе уже есть подгруппы — сначала перенесите их',
    };
  }
  return { ok: true };
}

// ─────────────────────────────────────────── единицы

export type Unit = 'PIECE' | 'KG' | 'L' | 'PORTION';

export const UNIT_RU: Record<Unit, string> = {
  PIECE: 'шт',
  KG: 'кг',
  L: 'л',
  PORTION: 'порция',
};

/** Как называется единица рядом с числом: «2 шт», «0,5 кг», «3 порции». */
export function unitLabel(unit: Unit, qty: number): string {
  if (unit !== 'PORTION') return UNIT_RU[unit];
  const n = Math.abs(Math.round(qty));
  const last = n % 10;
  const teen = n % 100 >= 11 && n % 100 <= 14;
  if (!teen && last === 1) return 'порция';
  if (!teen && last >= 2 && last <= 4) return 'порции';
  return 'порций';
}

/**
 * Дробное количество имеет смысл не для всего: полкило мяса бывает,
 * полбутылки в чеке — нет. Касса по этому решает, показывать ли
 * весовой ввод или простой счётчик.
 */
export function allowsFraction(unit: Unit): boolean {
  return unit === 'KG' || unit === 'L';
}

/**
 * Шаг изменения количества: штуки и порции считаем целыми,
 * вес и объём — сотыми.
 */
export function qtyStep(unit: Unit): number {
  return allowsFraction(unit) ? 0.01 : 1;
}

// ─────────────────────────────────────────── правила группы

export type GroupRules = {
  /** наценка, %; null — брать у родителя */
  markupPct: number | null;
  /** можно ли давать скидку на товары группы */
  allowDiscount: boolean;
  /** списывать ингредиенты при продаже */
  writeOffOnSale: boolean;
};

/**
 * Правила подгруппы с наследованием от группы.
 *
 * Наценка наследуется: у «Бара» 300%, и «Напитки» внутри берут те же
 * 300%, пока не задано своё. Это экономит владельцу десятки касаний.
 *
 * А вот ЗАПРЕТЫ не наследуются послаблением: если у «Бара» скидки
 * запрещены, подгруппа не может их разрешить. Запрет на алкоголь
 * ставят один раз наверху, и обойти его снизу нельзя — иначе смысл
 * запрета теряется.
 */
export function resolveGroupRules(
  own: Partial<GroupRules>,
  parent: Partial<GroupRules> | null,
): GroupRules {
  return {
    markupPct: own.markupPct ?? parent?.markupPct ?? null,
    // Разрешение требует согласия обоих: своего и родительского
    allowDiscount: (own.allowDiscount ?? true) && (parent?.allowDiscount ?? true),
    writeOffOnSale: (own.writeOffOnSale ?? true) && (parent?.writeOffOnSale ?? true),
  };
}

/**
 * Цена по себестоимости и наценке.
 *
 * Считаем в тиынах и округляем до тенге вверх: цена «1 234,56 ₸»
 * в меню не бывает, а округление вниз каждый раз отнимает у заведения
 * по копейке с блюда.
 */
export function priceFromCost(costTiyn: number, markupPct: number): number {
  if (costTiyn <= 0) return 0;
  const withMarkup = costTiyn * (1 + markupPct / 100);
  return Math.ceil(withMarkup / 100) * 100;
}

/**
 * Обратный счёт: какая наценка получилась при текущей цене.
 * Нужен, когда владелец правит цену руками и хочет понять,
 * во что это обошлось.
 */
export function markupFromPrice(costTiyn: number, priceTiyn: number): number | null {
  if (costTiyn <= 0) return null;
  return Math.round(((priceTiyn - costTiyn) / costTiyn) * 100);
}
